import { subscribeToDashboardThunk } from '@/common/noifier-module/NotifierThunks';
import { NotifierConnectionStatus } from '@/common/noifier-module/types';
import { useAppDispatch, useAppSelector } from '@/common/reducers/hooks/store.hook';
import { Loader2, RefreshCw } from 'lucide-react';
import React, { useEffect } from 'react';
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import ClusterOptimizerTabs from './components/tabs';
import { useClusterData } from './hooks/useClusterData';
import { CLUSTER_INFO } from './utils/types';

const ClusterOptimizer: React.FC = () => {
  const dispatch = useAppDispatch();

  const connectionStatus = useAppSelector((state) => state.NotifierReducer?.connectionStatus);

  
  const {
    nodePodsMap,
    nodeMetrics,
    podMetrics,
    podSessionMap,
    clusterMetrics,
    sessionStats,
    expandedNodes,
    showGatewayNodes,
    showNormalNodes,
    isLoading,
    isLoadingSessions,
    error,
    sessions,
    loadAllSessions,
    toggleNode,
    setShowGatewayNodes,
    setShowNormalNodes
  } = useClusterData();
  
  // For pagination status display
  const [paginationStatus, setPaginationStatus] = React.useState({ total: 0 });

  // Track mount state so the mount-effect load doesn't setState after unmount
  const isMountedRef = React.useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleLoadSessions = async () => {
    setPaginationStatus({ total: 0 });
    const updateInterval = setInterval(() => {
      const currentTotal = sessionStats.total;
      setPaginationStatus({ total: currentTotal });
    }, 500);

    try {
      await loadAllSessions();
    } finally {
      // Always clear the progress updater, even if loading throws
      clearInterval(updateInterval);
      if (isMountedRef.current) {
        setPaginationStatus({ total: sessionStats.total });
      }
    }
  };

  useEffect(() => {
    handleLoadSessions();
  }, []);

  useEffect(() => {
    if (connectionStatus === NotifierConnectionStatus.CONNECTED) {
      dispatch(subscribeToDashboardThunk());
    }
  }, [connectionStatus]);

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Cluster Optimizer</h1>
          <p className="text-slate-500 mt-1">
            Connected to <span className="font-semibold">gke_blueticks_{CLUSTER_INFO.region}_{CLUSTER_INFO.name}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleLoadSessions} 
            variant="outline" 
            size="sm"
            disabled={isLoadingSessions}
          >
            {isLoadingSessions ? (
              <div className="flex items-center">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading Sessions {paginationStatus.total > 0 ? `(${paginationStatus.total} loaded)` : ''}
              </div>
            ) : (
              <div className="flex items-center">
                <RefreshCw className="h-4 w-4 mr-2" /> 
                Load Sessions
              </div>
            )}
          </Button>
          <Badge variant="outline" className="ml-4 bg-green-100">
            Real-time updates via Watch API
          </Badge>
        </div>
      </div>
      
      {/* Tabbed Interface */}
      <ClusterOptimizerTabs
        nodePodsMap={nodePodsMap}
        nodeMetrics={nodeMetrics}
        podMetrics={podMetrics}
        podSessionMap={podSessionMap}
        clusterMetrics={clusterMetrics}
        sessionStats={sessionStats}
        expandedNodes={expandedNodes}
        showGatewayNodes={showGatewayNodes}
        showNormalNodes={showNormalNodes}
        isLoading={isLoading}
        isLoadingSessions={isLoadingSessions}
        error={error}
        sessions={sessions || []}
        toggleNode={toggleNode}
        setShowGatewayNodes={setShowGatewayNodes}
        setShowNormalNodes={setShowNormalNodes}
      />
    </div>
  );
};

export default ClusterOptimizer; 